import { FIELD_MAX_LENGTHS, TOAST_VALIDATION_MESSAGES, VALIDATION_MESSAGES } from 'shared'
import { z } from 'zod'
import { validateForm } from '@/lib/form-validation'

/**
 * オポチュニティプロパティのバリデーションスキーマ
 */
export const opportunityPropertiesSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, VALIDATION_MESSAGES.REQUIRED)
    .max(FIELD_MAX_LENGTHS.OPPORTUNITY_TITLE, VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.OPPORTUNITY_TITLE)),
  informationSource: z
    .string()
    .max(
      FIELD_MAX_LENGTHS.OPPORTUNITY_INFORMATION_SOURCE,
      VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.OPPORTUNITY_INFORMATION_SOURCE),
    ),
  description: z
    .string()
    .max(
      FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION,
      VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION),
    ),
  contactName: z
    .string()
    .max(
      FIELD_MAX_LENGTHS.OPPORTUNITY_CONTACT_NAME,
      VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.OPPORTUNITY_CONTACT_NAME),
    ),
})

/**
 * オポチュニティプロパティのフィールドエラーを取得
 */
export function getOpportunityPropertiesErrors(data: {
  title: string
  informationSource: string
  description: string
  contactName: string
}): Record<string, string> {
  return validateForm(opportunityPropertiesSchema, data).errors
}

/**
 * コメントのバリデーションスキーマ（必須+最大）
 */
const commentSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, TOAST_VALIDATION_MESSAGES.requiredInput('コメント'))
    .max(FIELD_MAX_LENGTHS.COMMENT, TOAST_VALIDATION_MESSAGES.maxLength('コメント', FIELD_MAX_LENGTHS.COMMENT)),
})

/**
 * コメントのバリデーションスキーマ（最大のみ）
 */
const commentMaxLengthSchema = z.object({
  content: z
    .string()
    .max(FIELD_MAX_LENGTHS.COMMENT, TOAST_VALIDATION_MESSAGES.maxLength('コメント', FIELD_MAX_LENGTHS.COMMENT)),
})

/**
 * コメント送信時のバリデーション（必須+最大）
 */
export function getCommentError(value: string): string | null {
  return validateForm(commentSchema, { content: value }).errors.content ?? null
}

/**
 * コメント入力中のバリデーション（最大のみ）
 */
export function getCommentMaxLengthError(value: string): string | null {
  return validateForm(commentMaxLengthSchema, { content: value }).errors.content ?? null
}

/**
 * コメントが有効かどうかをチェック（送信ボタンのdisabled用）
 */
export function isCommentValid(value: string): boolean {
  return commentSchema.safeParse({ content: value }).success
}

/**
 * 説明文・クローズ理由詳細の最大長スキーマ
 */
const descriptionMaxLengthSchema = z.object({
  description: z
    .string()
    .max(
      FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION,
      VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION),
    ),
})

/**
 * 説明文・クローズ理由詳細の最大長エラーを取得
 */
export function getDescriptionMaxLengthError(value: string): string | null {
  return validateForm(descriptionMaxLengthSchema, { description: value }).errors.description ?? null
}

/**
 * クローズ理由詳細スキーマ（必須+最大）
 */
const additionalCloseReasonRequiredSchema = z.object({
  description: z
    .string()
    .trim()
    .min(1, VALIDATION_MESSAGES.REQUIRED)
    .max(
      FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION,
      VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION),
    ),
})

/**
 * クローズ理由詳細が有効かどうかをチェック（必須時）
 */
export function isAdditionalCloseReasonValid(value: string, required: boolean): boolean {
  const schema = required ? additionalCloseReasonRequiredSchema : descriptionMaxLengthSchema
  return schema.safeParse({ description: value }).success
}

/**
 * G会社フィールドのスキーマ
 */
const gCompanyFieldSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1, VALIDATION_MESSAGES.REQUIRED)
    .max(FIELD_MAX_LENGTHS.NAME_STANDARD, VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.NAME_STANDARD)),
})

/**
 * G会社フィールドのバリデーション
 */
export function validateGCompanyField(value: string): string | null {
  return validateForm(gCompanyFieldSchema, { value }).errors.value ?? null
}
